<template>
  <div
    class="garage-hero-banner"
    :class="`garage-hero-banner--${template}`"
  >
    <heading-copy
      v-if="template === 'results'"
      class="garage-hero-banner__content"
      :classes="{
        heading: 'text-heading-2xl text-heading-4xl-desktop',
        subheading: 'text-heading-m text-heading-l-desktop',
      }"
      :content="{
        headingText: $string('garage.results.heading'),
        subheadingText: $string('garage.results.subheading'),
        textColour: 'light',
      }"
    />

    <garage-vehicle-details
      class="garage-hero-banner__vehicle"
      :template="garageVehicleDetailsTemplate"
      :vehicle="vehicle"
    />

    <div class="garage-hero-banner__buttons">
      <btn
        v-if="template === 'finder' && formattedVehicle?.resultsUrl"
        class="garage-hero-banner__button"
        :label="$string('garage.cta_labels.see_products')"
        modifiers="fill center"
        :url="vehicle.resultsUrl"
      />

      <template v-if="template === 'missing'">
        <form
          v-if="missingManualFormOpen"
          class="garage-hero-banner__form-group form-group"
          @submit="handleReportMissingManual"
        >
          <div class="form-group__field">
            <label
              class="
                garage-hero-banner__form-label
                form-group__label
                text-body-xs-bold
              "
              :for="`${formId}-email`"
            >
              <span v-text="$string('garage.missing_manual.email.label')" />

              <abbr
                :title="$string('general.required.label')"
                v-text="$string('general.required.symbol')"
              />
            </label>

            <input
              :id="`${formId}-email`"
              v-model="email"
              class="text-body-m-regular"
              :placeholder="$string('garage.missing_manual.email.placeholder')"
              required
              type="email"
            >
          </div>

          <p
            v-if="missingManualNote"
            class="
              garage-hero-banner__form-field
              form-group__field
              text-body-s-regular
            "
            v-html="missingManualNote"
          />

          <btn
            class="garage-hero-banner__button"
            :label="$string('garage.missing_manual.submit')"
            modes="dark"
            modifiers="fill center"
            type="submit"
          />
        </form>

        <btn
          v-else
          class="garage-hero-banner__button"
          :disabled="reportedMissingManual"
          :label="missingManualString"
          modes="dark"
          modifiers="fill center"
          type="button"
          @click="missingManualFormOpen = true"
        />
      </template>

      <btn
        v-if="template === 'modal' && formattedVehicle?.resultsUrl"
        class="garage-hero-banner__button"
        :label="$string('garage.cta_labels.shop_products')"
        modifiers="fill center"
        :url="formattedVehicle.resultsUrl"
      />

      <btn
        v-if="template === 'results'"
        class="garage-hero-banner__button"
        :label="$string('garage.cta_labels.search_again')"
        modifiers="fill center"
        type="button"
        @click="openFinderFlow('finder')"
      />

      <btn
        v-if="template === 'results'"
        class="garage-hero-banner__button"
        :label="$string('garage.cta_labels.different_car')"
        modes="dark"
        modifiers="secondary center"
        type="button"
        @click="openFinderFlow('garage')"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Garage hero banner (garage-hero-banner)
 * -----------------------------------------------------------------------------
 * Banner displaying text, vehicle details, and CTA buttons.
 *
 * @param {String} [formId] - Unique ID for missing form.
 * @param {String} template - Template to control rendering of buttons,
 * `finder`, `missing`, `modal`, or `results`.
 * @param {Boolean|import("~stores/garage").Vehicle} vehicle - Vehicle.
 *
 */
import { mapActions, mapGetters } from 'vuex'

import Btn from '~global/btn/btn'
import GarageVehicleDetails from '~async/garage-vehicle-details/garage-vehicle-details'
import HeadingCopy from '~async/heading-copy/heading-copy'

import { trackBloomreachEvent } from '~/helpers/bloomreach'
import { buildEventParameters, validateVehicle } from '~/helpers/garage'

export default {
  name: 'GarageHeroBanner',

  components: {
    Btn,
    GarageVehicleDetails,
    HeadingCopy,
  },

  props: {
    formId: {
      type: String,
      default: '',
    },
    template: {
      type: String,
      default: 'results',
      required: true,
      validator: (value) => ['finder', 'missing', 'modal', 'results'].includes(value),
    },
    vehicle: {
      type: [Boolean, Object],
      default: false,
      required: true,
      validator: validateVehicle,
    },
  },

  data() {
    return {
      email: '',
      missingManualFormOpen: false,
      reportedMissingManual: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFormattedVehicle: 'garage/getFormattedVehicle',
    }),

    /**
     * Compute formatted vehicle.
     * @returns {import("~stores/garage").FormattedVehicle}
     */
    formattedVehicle() {
      return this.getFormattedVehicle(this.vehicle)
    },

    /**
     * Compute garage vehicle details template.
     * @returns {String}
     */
    garageVehicleDetailsTemplate() {
      switch (this.template) {
        case 'modal':
          return 'hero-modal'

        case 'missing':
          return 'missing'

        default:
          return 'hero-results'
      }
    },

    /**
     * Compute missing manual note.
     * @returns {String}
     */
    missingManualNote() {
      if (this.template !== 'missing') {
        return ''
      }

      return this.$string('garage.missing_manual.note_html')
        .replaceAll('<a', '<a class="text-body-s-regular"')
    },

    /**
     * Compute missing manual string.
     * @returns {String}
     */
    missingManualString() {
      if (this.template !== 'missing') {
        return ''
      }

      return this.reportedMissingManual
        ? this.$string('garage.cta_labels.reported_missing_manual')
        : this.$string('garage.cta_labels.report_missing_manual')
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openFinderFlow: 'garage/openFinderFlow',
    }),

    /**
     * Handle report missing manual.
     */
    handleReportMissingManual() {
      const eventParameters = {
        ...buildEventParameters(this.vehicle),
        email: this.email,
      }

      trackBloomreachEvent('vehicle_search_non_saleable', eventParameters)
      this.reportedMissingManual = true
      this.missingManualFormOpen = false
    },
  },
}
</script>

<style lang="scss">
@import './garage-hero-banner';
</style>
